import { useRef, useEffect, useState, useCallback } from 'react';
import SignaturePadLib from 'signature_pad';
import { Button } from '@/app/components/ui/button';
import { Undo2, Trash2, Check } from 'lucide-react';

interface SignaturePadProps {
  onAccept: (dataUrl: string) => void;
  onClear?: () => void;
  readOnly?: boolean;
  signatureData?: string;
  width?: number;
  height?: number;
  acceptDisabled?: boolean;
}

export function SignaturePad({
  onAccept,
  onClear,
  readOnly = false,
  signatureData,
  width = 400,
  height = 150,
  acceptDisabled = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || readOnly) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }

    if (padRef.current) {
      padRef.current.clear();
      setIsEmpty(true);
    }
  }, [readOnly]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || readOnly) return;

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
    });

    pad.addEventListener('endStroke', () => {
      setIsEmpty(pad.isEmpty());
    });

    padRef.current = pad;
    resizeCanvas();

    return () => {
      pad.off();
    };
  }, [readOnly, resizeCanvas]);

  useEffect(() => {
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  const handleClear = () => {
    if (padRef.current) {
      padRef.current.clear();
      setIsEmpty(true);
      onClear?.();
    }
  };

  const handleUndo = () => {
    if (padRef.current) {
      const data = padRef.current.toData();
      if (data.length > 0) {
        data.pop();
        padRef.current.fromData(data);
        setIsEmpty(padRef.current.isEmpty());
      }
    }
  };

  const handleAccept = () => {
    if (padRef.current && !padRef.current.isEmpty()) {
      const dataUrl = padRef.current.toDataURL('image/png');
      onAccept(dataUrl);
    }
  };

  if (readOnly && signatureData) {
    const isValidImage = signatureData.startsWith('data:image/');
    if (!isValidImage) return null;
    return (
      <div
        className="border rounded-md bg-white inline-block"
        style={{ maxWidth: width, maxHeight: height }}
      >
        <img
          src={signatureData}
          alt="Signature"
          className="max-w-full max-h-full object-contain"
          style={{ maxWidth: width, maxHeight: height }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2" style={{ maxWidth: width }}>
      <div className="relative border rounded-md bg-white">
        <canvas
          ref={canvasRef}
          className="w-full touch-none cursor-crosshair"
          style={{ height }}
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-400 text-sm italic">Sign here</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleUndo}
          disabled={isEmpty}
        >
          <Undo2 className="h-3.5 w-3.5 mr-1" />
          Undo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={isEmpty}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" />
          Clear
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleAccept}
          disabled={isEmpty || acceptDisabled}
          className="ml-auto"
        >
          <Check className="h-3.5 w-3.5 mr-1" />
          Accept
        </Button>
      </div>
    </div>
  );
}
